import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { RiskManagementReportViewModel, RiskReportExposureCards } from "./RiskManagementReport.types";
import { buildRiskReportCoverage } from "./RiskManagementReport.utils";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";

const url = "/risk-management-report";

export function useRiskManagementReport() {
    const { translateShortMonth, translateString, translateNumber } = useTranslation();
    const { isLoading, data } = useDotLegalQuery<RiskManagementReportViewModel>(url);

    const riskAppetiteLimit = data?.maxRisksOverAppetite ?? 0;
    const risksAboveAppetite = data?.risksOverAppetite ?? 0;
    const coverage = buildRiskReportCoverage(data, translateString);
    const risksOverAppetiteByArea = (data?.risksOverAppetitePerArea ?? []).map((area) => ({ area: area.riskAreaName, value: area.count }));
    const countAboveAppetite = (data?.countAboveAppetitePerMonth ?? []).map((point) => ({
        x: translateShortMonth(new Date(point.month).getMonth()),
        y: point.count,
    }));

    // The three exposure cards, shown whenever exposure is configured (Financial or Scale). Financial reads the
    // sums; Scale reads the averages (ordinal values are never summed) and renders "value / upper bound". The
    // mitigation cost is money in both modes. Everything is pre-formatted here so the web view and the PDF render
    // identical strings.
    const exposureCards: RiskReportExposureCards | null = (() => {
        const exposure = data?.exposure;
        if (!data || !exposure || data.exposureMode === RiskExposureMode.None || exposure.withExposureCount === 0) return null;

        const financial = data.exposureMode === RiskExposureMode.Financial;
        const currency = data.exposureCurrencyCode ?? "";
        const money = (value: number) => `${currency} ${translateNumber(value)}`.trim();
        const scaleValue = (value: number) => `${translateNumber(value, { maximumFractionDigits: 2 })} / ${data.exposureScaleUpperBound}`;
        const exposureValue = (value: number) => (financial ? money(value) : scaleValue(value));
        const percent = (value: number) => `${translateNumber(value, { maximumFractionDigits: 0 })} %`;

        const gross = (financial ? exposure.grossSum : exposure.grossAverage) ?? 0;
        const afterMitigation = (financial ? exposure.afterMitigationSum : exposure.afterMitigationAverage) ?? 0;
        const cost = exposure.mitigationCostSum ?? 0;
        const planCount = exposure.afterMitigationPlanCount;

        // Positive = exposure fell after mitigation (good), negative = it rose (mitigation made it more
        // expensive). Only meaningful once active plans move the figure; at exactly 0 there is nothing to show.
        const changePercentage = gross > 0 ? Math.round(((gross - afterMitigation) / gross) * 100) : 0;

        const direction: "reduction" | "increase" = changePercentage > 0 ? "reduction" : "increase";
        const afterMitigationChange =
            planCount > 0 && changePercentage !== 0
                ? {
                      direction,
                      text: translateString(direction === "reduction" ? "riskManagementReportExposureReduction" : "riskManagementReportExposureIncrease", {
                          value: percent(Math.abs(changePercentage)),
                      }),
                  }
                : null;

        return {
            currentExposure: exposureValue(gross),
            currentExposureHeader: translateString(financial ? "riskManagementReportCurrentExposure" : "averageExposure"),
            afterMitigation: exposureValue(afterMitigation),
            afterMitigationChange,
            mitigationCosts: money(cost),
            mitigationCostsPlans: planCount > 0 ? translateString("riskManagementReportExposureAcrossPlans", { count: planCount }) : null,
        };
    })();

    return {
        isLoading,
        riskAppetiteLimit,
        risksAboveAppetite,
        isExceeded: risksAboveAppetite > riskAppetiteLimit,
        coverage,
        risksOverAppetiteByArea,
        countAboveAppetite,
        exposureCards,
    };
}
